import { findBioMentionTokens } from "./bioMentionText.ts";

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export interface ConvertUrlsToLinksOptions {
  /**
   * Server-validated @handle → absolute league profile URL. Only handles
   * present here are linkified; any other @token stays plain text (an
   * Instagram handle in a bio must not become a dead league link).
   */
  mentionLinks?: Record<string, string>;
}

/**
 * Escape a plain-text run, linkifying any @handle present in `mentionLinks`.
 * Mention links are internal navigations, so unlike URL links they open in
 * the same tab and carry no rel attributes.
 */
const escapeTextWithMentions = (
  segment: string,
  mentionLinks?: Record<string, string>,
) => {
  if (!mentionLinks) return escapeHtml(segment);
  let html = "";
  let lastIndex = 0;
  for (const token of findBioMentionTokens(segment)) {
    const href = mentionLinks[token.handle];
    if (!href) continue;
    html += escapeHtml(segment.slice(lastIndex, token.start));
    html += `<a href="${escapeHtml(href)}" class="underline">${escapeHtml(segment.slice(token.start, token.end))}</a>`;
    lastIndex = token.end;
  }
  html += escapeHtml(segment.slice(lastIndex));
  return html;
};

/**
 * Linkify https:// URLs — and, when `mentionLinks` is provided, validated
 * @handle mentions — in a plain-text string, returning HTML.
 *
 * The output is fed straight to dangerouslySetInnerHTML by every caller, so
 * EVERY character has to be escaped — not just the URLs. The previous version
 * escaped inside `String.replace`'s callback, which only ever runs on matched
 * URLs, leaving all surrounding text to pass through raw: a rec league
 * description of `<img src=x onerror=...>` executed on its public profile.
 */
export const convertUrlsToLinks = (
  text: string,
  options: ConvertUrlsToLinksOptions = {},
) => {
  const urlRegex = /(https:\/\/[^\s]+)/g;
  const MAX_URL_LENGTH = 200; // arbitrary number, we can change this if we want

  let html = "";
  let lastIndex = 0;

  for (const match of text.matchAll(urlRegex)) {
    const url = match[0];
    const start = match.index ?? 0;

    // Plain text between the previous match and this one.
    html += escapeTextWithMentions(text.slice(lastIndex, start), options.mentionLinks);
    lastIndex = start + url.length;

    // Reject extremely long URLs, and anything that isn't really https.
    let isLinkable = url.length <= MAX_URL_LENGTH;
    if (isLinkable) {
      try {
        isLinkable = new URL(url).protocol === "https:";
      } catch {
        isLinkable = false; // Not a valid URL — render as text
      }
    }

    if (!isLinkable) {
      html += escapeHtml(url);
      continue;
    }

    const displayUrl = url.length > 50 ? `${url.substring(0, 47)}...` : url;
    // Links inherit the surrounding text color and are marked by underline
    // only — matches renderTextWithLinks and the mobile LinkifiedText.
    html += `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer nofollow" class="underline">${escapeHtml(displayUrl)}</a>`;
  }

  // Trailing plain text after the last match (or the whole string if none).
  html += escapeTextWithMentions(text.slice(lastIndex), options.mentionLinks);

  return html;
};
